import { useEffect, useState } from "react";

type InViewProps = {
  triggerOnce?: boolean;
  /**
   * A value from 0 to 1 indicating the percentage of the element that must be visible to trigger the inView state
   * - 0: Triggers as soon as 1px is visible
   * - 0.5: Triggers when 50% is visible
   * - 1: Triggers when 100% is visible
   */
  threshold?: number;
  /**
   * CSS margin applied to the viewport bounding box (e.g., "50px", "0px 0px -100px 0px")
   * - Positive values expand the detection area (triggers before the element is fully in view)
   * - Negative values shrink the detection area (triggers only after the element crosses further in)
   */
  rootMargin?: string;
};

type State = {
  inView: boolean;
  entry?: IntersectionObserverEntry;
  ref: HTMLElement | null;
};

export function useInView({
  triggerOnce,
  threshold = 0,
  rootMargin,
}: InViewProps) {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<State>({
    inView: false,
    entry: undefined,
    ref: null,
  });

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          entry?.isIntersecting ||
          // threshold 0 can be used to be notified at 1px going into viewport for IntersectionObserver options but intersectionRatio still need to be greater than 0 to be considered in view
          (entry?.intersectionRatio &&
            entry.intersectionRatio > 0 &&
            entry.intersectionRatio >= threshold)
        ) {
          setState({
            inView: true,
            entry,
            ref,
          });
          if (triggerOnce && entry.isIntersecting) observer.unobserve(ref);
        } else {
          setState({
            inView: false,
            entry,
            ref: null,
          });
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(ref);

    return () => {
      observer.unobserve(ref);
    };
  }, [threshold, ref, triggerOnce, rootMargin]);

  return { ref: setRef, inView: state.inView, currentRef: state.ref };
}
